/** @format */

import { styled } from "@mui/material/styles";
import Page from "../../components/Page";
import {
  Card,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import Iconify from "../../components/Iconify";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import User from "../../dao/User";
import swal from "../../reusables/CustomSwal";

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(12, 0),
}));

export default function PasswordForm() {
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  let email = searchParams.get("m");
  let token = searchParams.get("token");

  const [userData, setUserData] = useState(null);

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onFormSubmit = async (values) => {
    try {
      await User.claimToken(email, token, values.password);

      await swal.fire({
        title: "Success",
        text: "User data is saved.",
        icon: "success",
        confirmButtonText: "Ok",
      });

      navigate("/login", { replace: true });
    } catch (e) {
      await swal.fire({
        title: "Error",
        text: e.error_message ? e.error_message : "Invalid Link",
        icon: "error",
        confirmButtonText: "Ok",
      });
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const RegisterSchema = Yup.object().shape({
    password: Yup.string().required("Password is required."),
    confirmPassword: Yup.string()
      .required("Please re-enter the password.")
      .oneOf([Yup.ref("password"), null], "Password does not match."),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: RegisterSchema,
    onSubmit: (values) => onFormSubmit(values),
  });

  const getUserData = async () => {
    try {
      let result = await User.getByEmailAndToken(email, token);
      setUserData(result);
      // console.log("QR", result);
    } catch (e) {
      await swal.fire({
        title: "Error",
        text: e.error_message ? e.error_message : "Invalid Link",
        icon: "error",
        confirmButtonText: "Ok",
      });

      navigate("/login", { replace: true });
      console.log(e);
    }
  };

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <RootStyle title="Registration">
      <Container maxWidth="sm">
        <ContentStyle>
          <Stack sx={{ mb: 5 }}>
            <img src={"/static/logo-infomedia.png"} alt="logo-infomedia" />
          </Stack>
          <Card sx={{ p: 4 }}>
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Stack spacing={3} sx={{ mb: 5 }}>
                  <Stack>
                    <Typography variant="h5" sx={{ textAlign: "center" }}>
                      Welcome to Infomedia!
                    </Typography>
                    <Typography variant="p" sx={{ textAlign: "center" }}>
                      Complete the registration form by filling in your
                      password.
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      disabled
                      label="First Name"
                      value={userData?.profile?.first_name}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      disabled
                      label="Last Name"
                      value={userData?.profile?.last_name}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    disabled
                    label="Email"
                    autoComplete="email"
                    value={userData?.email}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    disabled
                    label="Phone Broadcast"
                    autoComplete="tel"
                    value={userData?.profile?.phone_number}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    label="Password*"
                    autoComplete="off"
                    {...getFieldProps("password")}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowPassword((prev) => !prev)}
                          >
                            <Iconify
                              icon={
                                showPassword
                                  ? "eva:eye-outline"
                                  : "eva:eye-off-outline"
                              }
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm Password*"
                    autoComplete="new-password"
                    {...getFieldProps("confirmPassword")}
                    error={Boolean(
                      touched.confirmPassword && errors.confirmPassword
                    )}
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            <Iconify
                              icon={
                                showConfirmPassword
                                  ? "eva:eye-outline"
                                  : "eva:eye-off-outline"
                              }
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <LoadingButton
                    size="large"
                    type="submit"
                    style={{
                      marginTop: 30,
                    }}
                    variant="contained"
                    loading={isSubmitting}
                  >
                    <Iconify
                      icon="eva:save-outline"
                      width={22}
                      height={22}
                      sx={{ marginRight: 1 }}
                    />
                    Submit
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Card>
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
